import { useQueryClient } from '@tanstack/react-query';
import type { UserDto } from '@restaurant/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, onAuthFailure } from './api';

interface AuthState {
  user: UserDto | null;
  status: 'loading' | 'offline' | 'authenticated' | 'anonymous';
  login(email: string, password: string, remember?: boolean): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<UserDto | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  // Silent sign-in on load: the refresh cookie/token (if any) mints a new access token.
  // If the server is unreachable (restart, Wi-Fi blip) keep retrying instead of showing the login screen.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (n: number) => {
      const result = await api.auth.refreshDetailed();
      if (cancelled) return;
      if (result === 'offline') {
        setStatus('offline');
        timer = setTimeout(() => void attempt(n + 1), Math.min(30_000, 1000 * 2 ** Math.min(n, 5)));
        return;
      }
      if (result === 'invalid') return setStatus('anonymous');
      try {
        setUser(await api.auth.me());
        setStatus('authenticated');
      } catch {
        setStatus('anonymous');
      }
    };
    void attempt(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(
    () =>
      onAuthFailure(() => {
        setUser(null);
        setStatus('anonymous');
        qc.clear();
      }),
    [qc],
  );

  const login = useCallback(async (email: string, password: string, remember = true) => {
    const res = await api.auth.login({ email, password, remember });
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await api.auth.logout();
    setUser(null);
    setStatus('anonymous');
    qc.clear();
  }, [qc]);

  const value = useMemo(() => ({ user, status, login, logout }), [user, status, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
